import { Box, MenuItem, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { noop } from "lodash";
import { ReactElement } from "react";
import * as Yup from "yup";
import { z } from "zod";

import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import Select from "~frontend/components/forms/FormSelect";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import { FormikNgpVanSavedListAutocomplete } from "~frontend/components/forms/NgpVanObjectAutocomplete/NgpVanSavedListAutocomplete";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import theme from "~frontend/theme";

import { NgpVanIntegrationDatabaseLabel } from ".";
import ContactImportFormSubmitButtons from "../ContactImportFormSubmitButtons";

export const FormattedSavedListOptionSchema = z.object({
  name: z.string(),
  id: z.string(),
  savedListId: z.number(),
  description: z.string().nullable(),
  listCount: z.number(),
});

export type FormattedSavedListOption = z.infer<
  typeof FormattedSavedListOptionSchema
>;

export default function ImportForm({
  onSubmit,
  availableIntegrationAndDbModes,
  initialValues,
  readOnly,
  resetContactSourceSettings,
  selectedIntegrationAndDbMode,
  setSelectedIntegrationAndDbMode,
  ngpVanCampaignIntegrationEnabled,
}: {
  onSubmit?: (values: any) => void;
  availableIntegrationAndDbModes: NgpVanIntegrationDatabaseLabel[];
  initialValues: any;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
  selectedIntegrationAndDbMode: string;
  setSelectedIntegrationAndDbMode: (newVal: string) => void;
  ngpVanCampaignIntegrationEnabled: boolean;
}): ReactElement {
  const showIntegrationPicker = ngpVanCampaignIntegrationEnabled
    ? false
    : availableIntegrationAndDbModes.length != 1;

  const contactLimit = useFeatureFlag("contactLimit");

  if (!availableIntegrationAndDbModes.length) {
    return <div />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        integrationIdAndDbMode: Yup.string().required("Required"),
        ngpVanSavedListId: Yup.number().required("Required"),
        ngpVanSavedListCount: Yup.number()
          .required("Required")
          .lessThan(
            contactLimit,
            ({ less }) => `Cannot import more than ${less} contacts`
          ),
      })}
      onSubmit={onSubmit || noop}
    >
      {({ dirty, setFieldValue, values }) => (
        <Form>
          <RouteLeavingGuard when={dirty} />
          <FormikErrorBanner />
          {showIntegrationPicker && (
            <Select
              disabled={readOnly}
              fullWidth
              label="Integration"
              name="integrationIdAndDbModeSelector"
              onChange={async ev => {
                const selectedValue = ev.target.value;
                setSelectedIntegrationAndDbMode(selectedValue);
                await setFieldValue("integrationIdAndDbMode", selectedValue);
              }}
              value={selectedIntegrationAndDbMode}
              withNoneOption={false}
            >
              {availableIntegrationAndDbModes.map(integrationDbTuple => (
                <MenuItem
                  value={integrationDbTuple.id}
                  key={integrationDbTuple.id}
                >
                  {integrationDbTuple.label}
                </MenuItem>
              ))}
            </Select>
          )}
          <Field type="hidden" name="integrationIdAndDbMode" />

          {values.integrationIdAndDbMode && (
            <FormikNgpVanSavedListAutocomplete
              label="Saved List"
              name="ngpVanSavedListId"
              listCountName="ngpVanSavedListCount"
              databaseMode={values.integrationIdAndDbMode.split("|")[1]}
              integrationId={values.integrationIdAndDbMode.split("|")[0]}
              readOnly={readOnly}
            />
          )}

          <Typography variant="body2" sx={{ marginBottom: theme.spacing(1) }}>
            Don't see your list in the search bar? Check out our guide on the
            VAN Integration for troubleshooting help.
          </Typography>
          {onSubmit && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingTop: theme.spacing(1),
              }}
            >
              <ContactImportFormSubmitButtons
                resetContactSourceSettings={
                  resetContactSourceSettings
                    ? () => {
                        setSelectedIntegrationAndDbMode(
                          initialValues.integrationIdAndDbMode
                        );
                        resetContactSourceSettings();
                      }
                    : null
                }
              />
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
}
